import {
  build_case_form_sections,
  build_stage_timeline,
  build_summary_rows,
  fetch_queue_onboarding_cases,
  hydrate_case_detail
} from '@/services/onboarding_journey_service';
import {
  WORKFLOW_ACTION_STATE,
  workflowActionComputed,
  workflowActionMethods
} from '@/services/onboarding_workflow_action_service';
import {
  tablePaginationComputed,
  tablePaginationMethods,
  tablePaginationState
} from '@/services/table_pagination_service';
import { resolve_onboarding_journey_filter_key } from '@/services/onboarding_journey_filter_service';
import {
  hide_onboarding_system_loader_after_render,
  show_onboarding_system_loader
} from '@/views/onboarding_system_views/onboarding_ui_helpers';

const STATUS_TONE_BY_KEYWORD = [
  { keyword: 'approved', tone: 'success' },
  { keyword: 'rejected', tone: 'danger' },
  { keyword: 'applicant', tone: 'warning' },
  { keyword: 'intake', tone: 'warning' },
  { keyword: 'pending', tone: 'warning' }
];

function normalizeText(value = '') {
  return String(value || '').trim().toLowerCase();
}

function uniqueSubtitleParts(parts = []) {
  const seen_parts = new Set();
  return parts
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .filter((part) => {
      const normalized_part = normalizeText(part);
      if (seen_parts.has(normalized_part)) return false;
      seen_parts.add(normalized_part);
      return true;
    });
}

function matchesJourney(case_item = {}, journey_filter = '') {
  const normalized_filter = normalizeText(journey_filter);
  if (!normalized_filter) return true;

  const candidate_values = [
    case_item?.entity_type_id,
    case_item?.journey_tone,
    case_item?.journey_key,
    case_item?.journey
  ];
  return candidate_values.some((value) => normalizeText(value) === normalized_filter);
}

const getStatusCandidates = (case_item = {}) => {
  const workflow_statuses = Array.isArray(case_item?.workflow_statuses) ? case_item.workflow_statuses : [];
  return [
    case_item?.stage,
    case_item?.detail,
    case_item?.current_workflow_status,
    case_item?.activation,
    case_item?.workflow_display_stage,
    ...workflow_statuses.map((status) => status?.status_name || status?.workflow_status_name || status?.name || status?.status)
  ].filter(Boolean);
};

const matchesGateStatus = (case_item = {}, target_status = '') => {
  const normalized_target = normalizeText(target_status);
  if (!normalized_target) return false;

  return getStatusCandidates(case_item).some((value) => normalizeText(value).includes(normalized_target));
};

const buildStatusBadgeTone = (case_item = {}, status_filter = '') => {
  if (case_item?.activation_tone === 'ready') return 'success';
  if (case_item?.activation_tone === 'blocked') return 'danger';

  const normalized_filter = normalizeText(status_filter);
  return STATUS_TONE_BY_KEYWORD.find((item) => normalized_filter.includes(item.keyword))?.tone || 'warning';
};

const buildStatusBadgeLabel = (case_item = {}, status_filter = '') => (
  case_item?.activation
  || case_item?.stage
  || status_filter
  || 'In progress'
);

function normalizeProgress(value) {
  const progress = Number(value);
  if (!Number.isFinite(progress)) return 0;
  return Math.max(0, Math.min(100, Math.round(progress)));
}

function buildProgressStyle(progress) {
  const style = {
    width: `${normalizeProgress(progress)}%`
  };
  return style;
}

function getProgressBarClass(tone = '') {
  if (tone === 'success') return 'bg-success';
  if (tone === 'warning') return 'bg-warning';
  return 'bg-info';
}

function tab_matches_profile(tab = {}) {
  const label = normalizeText(`${tab?.id || ''} ${tab?.label || ''}`);
  return ['profile', 'personal', 'applicant'].some((word) => label.includes(word));
}

function get_question_document_url(question = {}) {
  return String(question?.file_url || '').trim();
}

function build_question_document_preview(question = {}, file_url = '') {
  const preview = {
    title: String(question?.answer || question?.label || 'Document').trim(),
    url: file_url
  };
  return preview;
}

export default {
  name: 'OnboardingGateStatusQueue',
  data: () => ({
    active_filter: 'all',
    is_loading_cases: false,
    cases_error: '',
    status_rows: [],
    selected_case_summary: null,
    active_summary_detail_tab: 'summary',
    is_summary_detail_fullscreen: false,
    is_loading_summary_detail: false,
    summary_rows: [],
    summary_stage_timeline: [],
    summary_case_form_sections: [],
    selected_document_preview: null,
    summary_detail_tabs: [
      { id: 'summary', label: 'Summary', icon: 'bi-grid' }
    ],
    ...WORKFLOW_ACTION_STATE,
    ...tablePaginationState(),
    filters: [
      { label: 'All', count: '0', tone: 'all' },
      { label: 'Open', tone: 'open' },
      { label: 'Mine', tone: 'mine' },
      { label: 'Blocked', tone: 'blocked' }
    ]
  }),
  computed: {
    gate_status_label() {
      return String(this.$route.query.status || this.$route.meta?.onboardingTitle || 'Gate').trim();
    },
    active_journey_filter() {
      return resolve_onboarding_journey_filter_key(this.$route);
    },
    page_title() {
      return this.gate_status_label;
    },
    page_subtitle() {
      return `Review onboarded entities in the ${this.gate_status_label.toLowerCase()} gate, open a summary from the row, and use View to open the assigned form.`;
    },
    filtered_rows() {
      if (this.active_filter === 'open') {
        return this.filter_table_rows(this.status_rows.filter((item) => item.status_tone !== 'success'));
      }

      if (this.active_filter === 'blocked') {
        return this.filter_table_rows(this.status_rows.filter((item) => item.status_tone === 'danger'));
      }

      return this.filter_table_rows(this.status_rows);
    },
    table_total_rows() {
      return this.filtered_rows.length;
    },
    paginated_rows() {
      return this.paginate_table_rows(this.filtered_rows);
    },
    ...tablePaginationComputed,
    active_summary_form_section() {
      const forms = Array.isArray(this.summary_case_form_sections) ? this.summary_case_form_sections : [];
      for (const form of forms) {
        const matched_section = (Array.isArray(form?.sections) ? form.sections : [])
          .find((section) => this.summary_tab_id_for_section(form, section) === this.active_summary_detail_tab);

        if (matched_section) return matched_section;
      }

      return null;
    },
    ...workflowActionComputed
  },
  watch: {
    '$route.query.status': {
      immediate: false,
      handler() {
        this.handle_gate_status_change();
      }
    },
    active_journey_filter() {
      this.handle_gate_status_change();
    }
  },
  mounted() {
    this.load_status_rows();
    window.addEventListener('onboarding-data-updated', this.handle_onboarding_data_updated);
  },
  beforeUnmount() {
    window.removeEventListener('onboarding-data-updated', this.handle_onboarding_data_updated);
  },
  methods: {
    ...workflowActionMethods,
    handle_gate_status_change() {
      this.active_filter = 'all';
      this.close_case_summary();
      this.status_rows = [];
      this.filters = this.filters.map((filter) => (
        filter.tone === 'all' ? { ...filter, count: '0' } : filter
      ));
      this.load_status_rows();
    },
    get_workflow_action_case() {
      return this.selected_case_summary || null;
    },
    async on_workflow_action_success() {
      this.close_case_summary();
      await this.load_status_rows();
    },
    handle_onboarding_data_updated() {
      this.load_status_rows();
    },
    set_active_filter(tone) {
      this.active_filter = tone;
      this.reset_table_page();
    },
    ...tablePaginationMethods,
    summary_tab_id_for_section(form, section) {
      return `summary-section-${form?.id || form?.form_name || 'form'}-${section?.id || section?.label || 'item'}`;
    },
    build_summary_detail_tabs(form_sections = []) {
      const tabs = [{ id: 'summary', label: 'Summary', icon: 'bi-grid' }];
      form_sections.forEach((form) => {
        (Array.isArray(form?.sections) ? form.sections : []).forEach((section) => {
          tabs.push({
            id: this.summary_tab_id_for_section(form, section),
            label: section?.label || form?.form_name || 'Section',
            icon: this.summary_tab_icon_for_section(form, section)
          });
        });
      });
      return tabs;
    },
    open_question_document(question = {}) {
      const file_url = get_question_document_url(question);
      if (!file_url) return;
      this.selected_document_preview = build_question_document_preview(question, file_url);
    },
    close_document_preview() {
      this.selected_document_preview = null;
    },
    summary_tab_icon_for_section(form = {}, section = {}) {
      const label = normalizeText(`${form?.form_name || ''} ${section?.label || ''}`);
      return ['document', 'upload', 'attachment', 'file', 'evidence'].some((word) => label.includes(word))
        ? 'bi-folder'
        : 'bi-list-check';
    },
    build_row(case_item = {}) {
      const form_count = Array.isArray(case_item?.forms) ? case_item.forms.length : 0;
      const document_count = Array.isArray(case_item?.documents) ? case_item.documents.length : 0;
      const checks_count = Math.max(form_count, document_count, 1);
      const status_filter = this.gate_status_label;

      const row = {
        id: String(case_item?.application_id || case_item?.id || case_item?.entity_profile_id || case_item?.name || Math.random()),
        case_name: case_item?.name || 'Onboarding case',
        case_detail: case_item?.detail || case_item?.stage || '',
        journey: case_item?.journey || 'Onboarding',
        progress: normalizeProgress(case_item?.progress),
        progress_tone: case_item?.progress_tone || '',
        risk: case_item?.risk || case_item?.risk_level || 'Not assessed',
        risk_tone: case_item?.risk_tone || 'pending',
        verification_count: checks_count,
        open_count: case_item?.progress >= 100 ? 0 : checks_count,
        status: buildStatusBadgeLabel(case_item, status_filter),
        status_tone: buildStatusBadgeTone(case_item, status_filter),
        reviewer: case_item?.owner || case_item?.onboarding_owner_role_name || 'Unassigned',
        eta: case_item?.eta || '-',
        activation: case_item?.activation || buildStatusBadgeLabel(case_item, status_filter),
        activation_tone: case_item?.activation_tone || buildStatusBadgeTone(case_item, status_filter),
        case_item
      };

      return row;
    },
    async load_status_rows() {
      this.is_loading_cases = true;
      this.cases_error = '';
      show_onboarding_system_loader(`Loading ${this.gate_status_label.toLowerCase()} gate...`);

      try {
        const backend_filters = {};
        if (this.active_journey_filter) {
          backend_filters.journey = this.active_journey_filter;
          backend_filters.entity_type_id = this.active_journey_filter;
        }
        if (this.gate_status_label) {
          backend_filters.status = this.gate_status_label;
        }

        const cases = await fetch_queue_onboarding_cases(backend_filters);
        this.status_rows = cases
          .filter((case_item) => matchesJourney(case_item, this.active_journey_filter))
          .filter((case_item) => matchesGateStatus(case_item, this.gate_status_label))
          .map((case_item) => this.build_row(case_item));

        this.filters = this.filters.map((filter) => (
          filter.tone === 'all' ? { ...filter, count: String(this.status_rows.length) } : filter
        ));
      } catch (error) {
        this.status_rows = [];
        this.cases_error = error?.response?.data?.message || error?.message || `Unable to load ${this.gate_status_label.toLowerCase()} cases.`;
      } finally {
        this.is_loading_cases = false;
        hide_onboarding_system_loader_after_render(this);
      }
    },
    async open_case_summary(row_item = {}) {
      const case_item = row_item?.case_item;
      if (!case_item) return;

      show_onboarding_system_loader('Loading gate drawer...');
      this.reset_workflow_action_state();
      this.close_document_preview();
      this.selected_case_summary = case_item;
      this.active_summary_detail_tab = 'summary';
      this.is_summary_detail_fullscreen = false;
      this.summary_rows = build_summary_rows(case_item);
      this.summary_stage_timeline = build_stage_timeline(case_item);
      this.summary_case_form_sections = build_case_form_sections(case_item);
      this.summary_detail_tabs = this.build_summary_detail_tabs(this.summary_case_form_sections);
      this.is_loading_summary_detail = true;

      try {
        const hydrated_case = await hydrate_case_detail(case_item);
        this.selected_case_summary = hydrated_case;
        this.summary_rows = build_summary_rows(hydrated_case);
        this.summary_stage_timeline = build_stage_timeline(hydrated_case);
        this.summary_case_form_sections = build_case_form_sections(hydrated_case);
        this.summary_detail_tabs = this.build_summary_detail_tabs(this.summary_case_form_sections);
        await this.load_workflow_actions_for_case(hydrated_case);
      } catch {
        this.selected_case_summary = case_item;
        await this.load_workflow_actions_for_case(case_item);
      } finally {
        this.is_loading_summary_detail = false;
        hide_onboarding_system_loader_after_render(this);
      }
    },
    open_case_form(row_item = {}, event) {
      event?.stopPropagation?.();

      const case_item = row_item?.case_item;
      const application_id = Number(case_item?.application_id || 0);
      if (!application_id) return;

      show_onboarding_system_loader('Opening onboarding form...');
      this.$router.push({
        name: 'MyQueueForm',
        params: {
          applicationId: String(application_id)
        },
        query: {
          name: case_item?.name || row_item?.case_name || '',
          company: case_item?.company_name || '',
          entity_type_id: String(case_item?.entity_type_id || case_item?.threshold_entity_type_id_used || ''),
          journey: case_item?.journey || row_item?.journey || '',
          status: this.gate_status_label,
          application_form_id: '',
          source: `${normalizeText(this.gate_status_label).replace(/\s+/g, '-')}-gate`
        }
      });
    },
    close_case_summary() {
      this.selected_case_summary = null;
      this.active_summary_detail_tab = 'summary';
      this.is_summary_detail_fullscreen = false;
      this.close_document_preview();
      this.reset_workflow_action_state();
    },
    current_summary_action_tab() {
      return this.summary_detail_tabs[0] || null;
    },
    summary_reference_tab() {
      return this.summary_detail_tabs.find(tab_matches_profile) || null;
    },
    summary_current_action_copy() {
      const label = String(this.current_summary_action_tab()?.label || this.page_title || 'Current gate').trim();
      return `${label} - needs your review`;
    },
    summary_reference_value() {
      const application_id = String(this.selected_case_summary?.application_id || this.selected_case_summary?.id || '').trim();
      return application_id ? `#${application_id}` : 'Open application details';
    },
    set_summary_detail_tab(tab_id) {
      this.active_summary_detail_tab = tab_id;
      this.close_document_preview();
    },
    open_selected_case_profile() {
      const profile_tab = this.summary_detail_tabs.find(tab_matches_profile);
      this.active_summary_detail_tab = profile_tab?.id || 'summary';
    },
    toggle_summary_detail_fullscreen() {
      this.is_summary_detail_fullscreen = !this.is_summary_detail_fullscreen;
    },
    selected_case_summary_subtitle() {
      if (!this.selected_case_summary) return '';

      const subtitle_parts = uniqueSubtitleParts([
        this.selected_case_summary?.detail,
        this.selected_case_summary?.activation,
        this.selected_case_summary?.eta
      ]);

      return subtitle_parts.join(' · ') || this.selected_case_summary?.stage || '';
    },
    status_badge_class(tone) {
      if (['success', 'ready'].includes(tone)) return 'bg-success-subtle text-success-emphasis';
      if (['danger', 'blocked'].includes(tone)) return 'bg-danger-subtle text-danger-emphasis';
      if (['warning', 'pending', 'progress'].includes(tone)) return 'bg-warning-subtle text-warning-emphasis';
      return 'bg-secondary-subtle text-secondary-emphasis';
    },
    risk_badge_class(tone) {
      if (tone === 'low') return 'bg-success-subtle text-success-emphasis';
      if (tone === 'medium') return 'bg-warning-subtle text-warning-emphasis';
      if (tone === 'high') return 'bg-danger-subtle text-danger-emphasis';
      return 'bg-secondary-subtle text-secondary-emphasis';
    },
    progress_bar_class(tone) {
      return getProgressBarClass(tone);
    },
    progress_bar_style(progress) {
      return buildProgressStyle(progress);
    }
  }
};
